import { supabase } from "./supabaseClient";

const SUPABASE_URL = (import.meta as any).env.VITE_SUPABASE_URL || 'https://gdjfeumtqpjbroxqbfqv.supabase.co';
const ANON_KEY = (import.meta as any).env.VITE_SUPABASE_ANON_KEY || 'sb_publishable_qZLqtJBltKyNGhQoxYK_og_5z3vcp2P';

export type LivePhase = 'LOBBY' | 'QUESTION' | 'ENDED';

export interface LiveQuestion {
  id: string;
  question_text: any;
  question_type: string;
  config: any;
}
export interface LiveState {
  phase: LivePhase;
  question: LiveQuestion | null;
}
export interface LiveResults {
  question_id: string;
  total: number;
  counts: { value: string; count: number }[];
}

// Stable per-device token (one vote per device per question).
export function getDeviceToken(): string {
  const k = 'tinaney_live_token';
  let t = localStorage.getItem(k);
  if (!t) { t = (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem(k, t); }
  return t;
}

// Audience read of "which question is live" — via the cache-friendly edge endpoint
// (CDN-absorbed at scale), falling back to the RPC if the function isn't deployed.
export async function fetchLiveState(surveyId: string): Promise<LiveState> {
  try {
    const r = await fetch(`${SUPABASE_URL}/functions/v1/live-state?survey=${surveyId}`, {
      headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
    });
    if (r.ok) return await r.json();
  } catch { /* fall through to RPC */ }
  const { data, error } = await supabase.rpc('get_live_state', { p_survey: surveyId });
  if (error) throw error;
  return data as LiveState;
}

export async function castLiveVote(surveyId: string, questionId: string, value: any, token: string): Promise<{ ok: boolean; reason?: string }> {
  const { data, error } = await supabase.rpc('cast_live_vote', {
    p_survey: surveyId, p_question: questionId, p_value: value, p_token: token,
  });
  if (error) throw error;
  return data;
}

export async function getLiveResults(surveyId: string, questionId: string): Promise<LiveResults> {
  const { data, error } = await supabase.rpc('live_results', { p_survey: surveyId, p_question: questionId });
  if (error) throw error;
  return data as LiveResults;
}

// Host control.
export async function setLiveState(surveyId: string, phase: LivePhase, questionId?: string | null): Promise<void> {
  const { error } = await supabase.rpc('set_live_state', { p_survey: surveyId, p_phase: phase, p_question: questionId ?? null });
  if (error) throw error;
}

// ── Realtime broadcast: instant host→audience state changes (low-frequency events,
//    one message fanned to all subscribers — not per-vote). Audience also slow-polls
//    fetchLiveState as a fallback for missed messages / late joiners. ──────────────
export function subscribeLiveState(surveyId: string, onState: (s: LiveState) => void) {
  const channel = supabase.channel(`live:${surveyId}`, { config: { broadcast: { self: false } } });
  channel.on('broadcast', { event: 'state' }, ({ payload }) => onState(payload as LiveState));
  channel.subscribe();
  return channel;
}

export function sendLiveState(channel: any, state: LiveState) {
  channel?.send({ type: 'broadcast', event: 'state', payload: state });
}

export function unsubscribe(channel: any) {
  try { supabase.removeChannel(channel); } catch { /* ignore */ }
}
